/**
 * Box Truck Loads → Bidding. Lists SYLECTUS_EMAIL loads in this project that already have an email
 * thread (a bid was sent from the Loadboard's Draft Email). Reuses the loadboard table + drawer; the
 * drawer opens straight into the Draft Email tab so the conversation continues like Load Hub (reply +
 * respond via PrivateLoadEmailChat). Data comes from main-service (PG interactions), not live search.
 */
import { useCallback, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { Button, Pagination, SearchField, Select, Spinner } from '@/components/ds2'
import { BIDDING_PAGE_SIZE, useBoxTruckBids } from '../hooks/useBoxTruckBids'
import { BoxTruckTable } from '../components/BoxTruckTable'
import { BoxTruckLoadDrawer } from '../components/BoxTruckLoadDrawer'
import { loadKey } from '../utils/loadFields'
import { dispatcherLabel } from '../utils/bidFormat'
import { fmtTime } from '../utils/format'

export const BoxTruckBiddingPage = () => {
    const { bids, total, page, loading, error, lastUpdatedAt, refresh, goToPage, dispatchers, dispatcherId, setDispatcherId } = useBoxTruckBids()
    const [query, setQuery] = useState('')
    const [selectedKey, setSelectedKey] = useState<string | null>(null)

    const loads = useMemo(() => bids.map((b) => b.load), [bids])
    // Contact column thread state — each Bidding row IS a thread, so the icon always shows as the green
    // thread (with its unread badge), same as the loadboard.
    const emailThreads = useMemo(
        () => Object.fromEntries(bids.map((b) => [loadKey(b.load), { emailThreadId: b.emailThreadId, unreadCount: b.unreadCount }])),
        [bids]
    )
    // Drives the table's "Bid by" column: who on the team sent the bid this row represents.
    const bidders = useMemo(() => Object.fromEntries(bids.map((b) => [loadKey(b.load), b.bidders ?? []])), [bids])
    const bidderNamesByKey = useMemo(
        () => Object.fromEntries(Object.entries(bidders).map(([key, people]) => [key, people.map((p) => `${dispatcherLabel(p)} ${p.email ?? ''}`).join(' ')])),
        [bidders]
    )

    // "Everyone" first so clearing the filter is one click, then the team by name with their bid counts.
    //
    // The team list comes from its own endpoint, but the loaded rows are UNIONED into it: a picker that
    // renders only what that one request returned disappears entirely the moment it fails or the operator's
    // project has its bids under ids the lookup couldn't resolve — leaving no way to filter at all on a page
    // whose rows plainly show who bid. Anyone visible on screen is therefore always pickable.
    const dispatcherOptions = useMemo(() => {
        const byId = new Map<string, { label: string; value: string }>()
        for (const person of Object.values(bidders).flat()) {
            byId.set(person.id, { label: dispatcherLabel(person), value: person.id })
        }
        // The endpoint's entries win: they carry the whole team and the bid counts, not just this page.
        for (const person of dispatchers) {
            byId.set(person.id, { label: `${dispatcherLabel(person)}${person.bidCount ? ` (${person.bidCount})` : ''}`, value: person.id })
        }
        return [{ label: 'All dispatchers', value: '' }, ...[...byId.values()].sort((a, b) => a.label.localeCompare(b.label))]
    }, [dispatchers, bidders])

    const visibleLoads = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return loads
        return loads.filter((load) => {
            const haystack = [
                load.postersReferenceId,
                load.pickupCity,
                load.pickupStateProv,
                load.deliveryCity,
                load.deliveryStateProv,
                load.brokerCompanyName,
                // Typing a colleague's name here narrows the page in place; the picker beside it is the
                // filter that reaches the bids on pages this client has not fetched.
                bidderNamesByKey[loadKey(load)],
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(q)
        })
    }, [loads, query, bidderNamesByKey])

    const selectedLoad = useMemo(() => visibleLoads.find((l) => loadKey(l) === selectedKey) ?? null, [visibleLoads, selectedKey])
    const handleRowClick = useCallback((load: CanonicalLoad) => setSelectedKey(loadKey(load)), [])

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            <header className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
                <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-foreground">Bidding</h1>
                    <p className="mt-0.5 text-sm text-foreground-muted">
                        Loads you've bid on · <span className="font-medium text-foreground">{total} active</span>
                        {lastUpdatedAt && <span className="ml-2 text-xs text-foreground-muted">updated {fmtTime(lastUpdatedAt)}</span>}
                    </p>
                </div>
                <Button variant="secondary" size="md" loading={loading} onClick={refresh} prefix={<RefreshCw size={16} />}>
                    Refresh
                </Button>
            </header>

            <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
                <div className="w-full max-w-sm">
                    <SearchField value={query} onChange={setQuery} placeholder="Filter by ref, city, broker, or dispatcher…" />
                </div>
                <div className="w-56">
                    <Select
                        variant="secondary"
                        options={dispatcherOptions}
                        value={dispatcherId ?? ''}
                        onChange={(value) => setDispatcherId(value || null)}
                        placeholder="All dispatchers"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {error ? (
                    <ErrorState message={error} onRetry={refresh} />
                ) : loading && loads.length === 0 ? (
                    <LoadingState />
                ) : visibleLoads.length === 0 ? (
                    <EmptyState filtered={loads.length > 0 || !!dispatcherId || !!query.trim()} />
                ) : (
                    <BoxTruckTable loads={visibleLoads} selectedKey={selectedKey} onRowClick={handleRowClick} emailThreads={emailThreads} bidders={bidders} />
                )}
            </div>

            {total > BIDDING_PAGE_SIZE && (
                <div className="border-t border-border px-6 py-3">
                    <Pagination variant="secondary" totalItems={total} pageSize={BIDDING_PAGE_SIZE} page={page} onPageChange={goToPage} itemLabel="loads" />
                </div>
            )}

            <BoxTruckLoadDrawer load={selectedLoad} onClose={() => setSelectedKey(null)} />
        </div>
    )
}

const LoadingState = () => (
    <div className="flex flex-1 items-center justify-center py-24">
        <Spinner color="accent" size={24} />
        <span className="ml-2 text-sm text-foreground-muted">Loading bids…</span>
    </div>
)

/** `filtered` covers BOTH narrowing paths — the client-side text box and the server-side dispatcher pick.
 *  The dispatcher filter returns an empty page from the server, so judging by "are there loads on screen"
 *  alone told an operator who picked a colleague with no bids that the whole team had never bid. */
const EmptyState = ({ filtered }: { filtered: boolean }) => (
    <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-medium text-foreground">{filtered ? 'No bids match your filter' : 'No bids yet'}</p>
        <p className="mt-1 text-xs text-foreground-muted">{filtered ? 'Try clearing the filter.' : 'Send a bid from the Loadboard and it will appear here.'}</p>
    </div>
)

const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-medium text-danger">Couldn’t load bids</p>
        <p className="mt-1 max-w-sm text-xs text-foreground-muted">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">
            Retry
        </Button>
    </div>
)
